"use client";

import type { RefObject } from "react";
import { startTransition, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import Image from "next/image";
import { Bookmark, ChevronDown, ChevronUp, MessageCircle, Upload, User, Flag, X, MapPin, Clock } from "lucide-react";
import AuthModal from "@/components/ui/auth-modal";
import { BookmarkLoginModal, type BookmarkModalListing } from "@/components/search/product/bookmark-login-modal";
import { useSavedState } from "@/hooks/use-saved-state";
import { getAvatarUrl } from "@/lib/getAvatarUrl";
import { resolveStorageUrl } from "@/lib/storage/public-url";
import { imagePresetUrl, SQUARE_SM } from "@/lib/images/presets";
import { ShareSheet } from "@/components/share/share-sheet";
import type { ListingShareMeta } from "@/lib/story-cards";
import { ReportDialog } from "@/components/ui/report-dialog";
import MarketplaceVerticalListingCard, { type ListingForVerticalCard } from "@/components/feed/marketplace-vertical-listing-card";
import { UnifiedFollowButton } from "@/components/UnifiedFollowButton";
import { UnifiedAddToCartButton } from "@/components/UnifiedAddToCartButton";
import { ListingSplitCommerceCta } from "@/components/offers/listing-split-commerce-cta";
import { MakeOfferButton } from "@/components/offers/make-offer-button";
import { cn } from "@/lib/utils";
import { useMobile } from "@/hooks/use-mobile";
import { useMediaQuery } from "@/hooks/use-media-query";
import { Dialog, DialogContent, DialogTitle, DIALOG_CARD_CONTENT_BASE, DIALOG_CARD_OVERLAY_CLASS } from "@/components/ui/dialog";
import { ChatTabSlider } from "@/components/chat/ChatTabSlider";
import { ChatDialog } from "@/components/chat/ChatDialog";
import { OrderPartyCard, OrderPartyCardSkeleton } from "@/components/orders/order-party-card";
import { VerticalListingSuggestions } from "@/components/feed/vertical-listing-suggestions";
import { Skeleton } from "@/components/ui/skeleton";
import { ItemCardSkeletonRow } from "@/components/search/item-card-skeleton";
import { Button } from "@/components/ui/button";
import { useTranslations } from "next-intl";
import { useAuction } from "@/hooks/use-auction";
import { useOverlay } from "@/components/context/overlay-context";
import { Z } from "@/lib/overlay/z-scale";
import { TcgCardDetails, TcgCardDetailsDark } from "@/components/detailed-listing-view/tcg-card-details";
import type { TcgCardDetailsData } from "@/components/detailed-listing-view/tcg-card-details";
import { FEED_SNAP_INDEX_KEY_FOR_YOU } from "@/lib/feed-snap-index";
import { getCountryName } from "@/lib/paypal-countries";
import { recordImpression } from "@/lib/analytics/impression-buffer";

export interface MarketplaceVerticalSnapFeedProps {
  listings: ListingForVerticalCard[];
  currentUserId?: string;
  hasNextPage?: boolean;
  isLoadingMore?: boolean;
  onLoadMoreAction?: () => void;
  onFollowChangeAction?: () => void;
  onToggleSavedAction?: () => void;
  onMobileSheetChangeAction?: (open: boolean) => void;
  onScrollContainerRef?: (el: HTMLDivElement | null) => void;
  loadMoreSentinelRef?: RefObject<HTMLDivElement | null>;
  onRegisterScrollToTopAction?: (scrollToTop: (() => void) | null) => void;
  snapIndexStorageKey?: string;
}

export default function MarketplaceVerticalSnapFeed({
  listings,
  currentUserId,
  hasNextPage = false,
  isLoadingMore = false,
  onLoadMoreAction = () => {},
  onFollowChangeAction = () => {},
  onToggleSavedAction = () => {},
  onMobileSheetChangeAction = () => {},
  onScrollContainerRef,
  loadMoreSentinelRef,
  onRegisterScrollToTopAction,
  snapIndexStorageKey = FEED_SNAP_INDEX_KEY_FOR_YOU,
}: MarketplaceVerticalSnapFeedProps) {
  const tListing = useTranslations("listing");
  const tAuction = useTranslations("auction");
  const router = useRouter();
  const isMobile = useMobile();
  const isNarrowViewport = useMediaQuery("(max-width: 1043px)");
  const { overlay, openPanel, updatePanel, closePanel, openUtility } = useOverlay();
  const containerRef = useRef<HTMLDivElement | null>(null);
  const sentinelRef = useRef<HTMLDivElement | null>(null);
  const setContainerRef = useCallback(
    (el: HTMLDivElement | null) => {
      containerRef.current = el;
      onScrollContainerRef?.(el);
    },
    [onScrollContainerRef]
  );
  useEffect(() => {
    return () => onScrollContainerRef?.(null);
  }, [onScrollContainerRef]);
  const savedIndexRef = useRef(
    typeof window !== "undefined"
      ? parseInt(sessionStorage.getItem(snapIndexStorageKey) || "0", 10) || 0
      : 0
  );
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isInteracting, setIsInteracting] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [showBookmarkModal, setShowBookmarkModal] = useState(false);
  const [showBuyNowModal, setShowBuyNowModal] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [chatMode, setChatMode] = useState<"tab" | "direct">("tab");
  const [chatRoomName, setChatRoomName] = useState("");
  const [chatOtherProfile, setChatOtherProfile] = useState<{ username: string; avatar_url?: string | null } | null>(null);
  const [chatInitialDraft, setChatInitialDraft] = useState<{ text?: string; listing?: any; sendImmediately?: boolean } | null>(null);
  const [savesCount, setSavesCount] = useState<number>(0);
  const [reportDialogOpen, setReportDialogOpen] = useState(false);
  const [contentOpacity, setContentOpacity] = useState(1);
  const [displayedListingId, setDisplayedListingId] = useState<string | null>(null);
  const [actionsOpacity, setActionsOpacity] = useState(1);
  const [displayedActionsListingId, setDisplayedActionsListingId] = useState<string | null>(null);
  const [displayedSaved, setDisplayedSaved] = useState(false);
  const [displayedSavesCount, setDisplayedSavesCount] = useState(0);
  const [displayedAvatarUrl, setDisplayedAvatarUrl] = useState<string | null>(null);
  const [displayedIsOwner, setDisplayedIsOwner] = useState(false);
  const [displayedSellerUsername, setDisplayedSellerUsername] = useState<string>("seller");
  const [displayedSellerId, setDisplayedSellerId] = useState<string | null>(null);
  const [displayedSellerIsFollowing, setDisplayedSellerIsFollowing] = useState(false);
  const [detailsLoading, setDetailsLoading] = useState(false);
  const detailsShiftX = 0;
  const haloEnabled = false;
  const cardNodeByIndexRef = useRef<Map<number, HTMLDivElement | null>>(new Map());
  const showDetailsCallbacksRef = useRef<Map<number, (show: boolean) => void>>(new Map());
  const tailLoadMoreArmedRef = useRef(true);
  const tailPlaceholderCount = hasNextPage || isLoadingMore ? 1 : 0;
  const effectiveLength = listings.length + tailPlaceholderCount;
  const haloAHostRef = useRef<HTMLDivElement | null>(null);
  const haloABgRef = useRef<HTMLDivElement | null>(null);
  const haloAUrlRef = useRef<string | null>(null);
  const haloAGeomRef = useRef<{ left: number; top: number; width: number; height: number; opacity: number } | null>(null);
  const haloBHostRef = useRef<HTMLDivElement | null>(null);
  const haloBBgRef = useRef<HTMLDivElement | null>(null);
  const haloBUrlRef = useRef<string | null>(null);
  const haloBGeomRef = useRef<{ left: number; top: number; width: number; height: number; opacity: number } | null>(null);
  const currentListing = listings[Math.min(Math.max(currentIndex, 0), Math.max(0, listings.length - 1))];
  // FSM-derived (spec §E 1.5, PanelOverlay `feed-details`): no local
  // open/closed state for the desktop panel anymore. Gated on `!isMobile` to
  // reproduce the pre-migration split exactly — the mobile bottom sheet
  // (marketplace-vertical-listing-card.tsx) owns the SAME panel kind for its
  // own breakpoint; the type alone can't tell which presentation opened it
  // (§C.1 — one canonical kind), so each presentation narrows by the same
  // `useMobile()` breakpoint hook the other already uses. Deliberately loose
  // on `listingId` (unlike the card's per-listing match): only ONE desktop
  // panel instance exists per feed, its content always follows `currentListing`
  // (not the FSM's stored id — see the id-sync effect below), so a strict
  // match would flicker the panel closed for one render on every scroll step
  // while the sync effect catches up. Mutual exclusion with other panel kinds
  // (profile, chat, ...) still holds: `overlay.panel.kind` can only be one
  // value at a time.
  const showDetailsPanel = !isMobile && overlay.panel.kind === "feed-details";
  const currentSellerUsername = currentListing?.sellerUsername ?? "seller";
  const currentAvatarUrl = getAvatarUrl(currentListing?.sellerAvatarUrl ?? null);
  const currentIsOwner = Boolean(currentUserId && currentListing && currentListing.user_id === currentUserId);
  // Phase 3 (2026-06-29): "offers enabled" is purely a function of format.
  const canFeedSplit =
    !currentIsOwner &&
    currentListing?.listing_format === "fixed_price_with_offers";
  const showOffersOnlyFeedCta = !currentIsOwner && currentListing?.listing_format === "offers_only";
  const currentIsAuction =
    currentListing?.listing_format === "auction" ||
    (currentListing?.auction_start_price != null && currentListing?.listing_format !== "fixed_price");

  // Realtime auction state for the currently visible slide (pg_changes path; no broadcast for feed)
  const currentAuctionSlot = useAuction(
    currentIsAuction && currentListing ? currentListing.id : null,
    {
      initial: currentIsAuction && currentListing ? {
        highestBid: currentListing.auction_highest_bid != null ? Number(currentListing.auction_highest_bid) : null,
        highestBidderId: null,
        bidCount: currentListing.auction_bid_count != null ? Number(currentListing.auction_bid_count) : 0,
        endAt: currentListing.auction_end_at ?? null,
        auctionStatus: currentListing.auction_status ?? null,
        eventVersion: 0,
      } : undefined,
    }
  );

  // Effective values: prefer realtime slot, fall back to SSR listing props
  const currentEffectiveHighestBid = currentAuctionSlot?.highestBid ?? (currentListing?.auction_highest_bid != null ? Number(currentListing.auction_highest_bid) : null);
  const currentEffectiveAuctionStatus = currentAuctionSlot?.auctionStatus ?? currentListing?.auction_status;
  // TODO: re-enable when inline feed bidding is restored
  // const currentIsAuctionSeller = Boolean(
  //   currentIsAuction && currentListing && currentUserId && currentListing.user_id === currentUserId,
  // );
  // const currentCanBidAuction = Boolean(
  //   currentIsAuction && !currentIsAuctionEnded && currentUserId && !currentIsAuctionSeller,
  // );
  const currentAuctionDisplayPrice = useMemo(() => {
    if (!currentIsAuction || !currentListing) return null;
    const highest = currentEffectiveHighestBid;
    const start =
      highest == null && currentListing.auction_start_price != null
        ? Number(currentListing.auction_start_price)
        : null;
    const fallback = highest ?? start ?? Number(currentListing.price);
    return Number.isFinite(fallback) ? fallback : null;
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentIsAuction, currentListing, currentEffectiveHighestBid]);
  const handleInteractionStart = useCallback(() => setIsInteracting(true), []);
  const handleInteractionEnd = useCallback(() => setIsInteracting(false), []);

  const bookmarkListing: BookmarkModalListing | null = useMemo(() => {
    if (!currentListing) return null;
    return {
      id: currentListing.id,
      title: currentListing.title,
      price: currentListing.price,
      currency: currentListing.currency ?? null,
      image_urls: currentListing.image_urls,
      description: currentListing.description,
      condition: currentListing.condition ?? "unknown",
      grade: currentListing.grade ?? "N/A",
      created_at: currentListing.created_at,
      username: currentListing.sellerUsername,
      avatar_url: currentListing.sellerAvatarUrl,
      scheduledPublishAt: currentListing.scheduled_publish_at ?? null,
    };
  }, [currentListing]);

  const updateHaloFromScroll = useCallback(() => {
    if (!haloEnabled) return;
    const el = containerRef.current;
    if (!el) return;
    const h = el.clientHeight;
    if (!h) return;

    const progress = el.scrollTop / h;

    const baseOpacity = 0.4;

    const resolveLayer = (idx: number | null) => {
      if (idx === null || idx < 0 || idx >= listings.length) return null;
      const url = listings[idx]?.image_urls?.[0] ?? null;
      if (!url) return null;

      const containerRect = el.getBoundingClientRect();
      if (!Number.isFinite(containerRect.top) || !Number.isFinite(containerRect.bottom)) return null;
      const viewportTop = containerRect.top;
      const viewportBottom = containerRect.bottom;

      const node = cardNodeByIndexRef.current.get(idx) ?? null;
      if (!node) return null;
      const rect = node.getBoundingClientRect();
      if (!Number.isFinite(rect.left) || !Number.isFinite(rect.top) || rect.width <= 0 || rect.height <= 0) return null;

      const visiblePx = Math.min(viewportBottom, rect.bottom) - Math.max(viewportTop, rect.top);
      const visibleFrac = Math.min(1, Math.max(0, visiblePx / rect.height));

      return {
        url,
        left: rect.left,
        top: rect.top,
        width: rect.width,
        height: rect.height,
        visibleFrac,
      };
    };

    const base = Math.round(progress);
    const candidates = [base - 2, base - 1, base, base + 1, base + 2];
    const resolved = candidates
      .map((idx) => resolveLayer(idx))
      .filter(
        (v): v is { url: string; left: number; top: number; width: number; height: number; visibleFrac: number } =>
          v !== null && v.visibleFrac > 0,
      )
      .sort((a, b) => b.visibleFrac - a.visibleFrac);
    const primary = resolved[0] ?? null;
    const secondary = resolved[1] ?? null;
    const primaryOpacity = primary ? baseOpacity * primary.visibleFrac * primary.visibleFrac : 0;
    const secondaryOpacity = secondary ? baseOpacity * secondary.visibleFrac * secondary.visibleFrac : 0;

    const applyLayer = (
      host: HTMLDivElement | null,
      bg: HTMLDivElement | null,
      urlRef: { current: string | null },
      geomRef: { current: { left: number; top: number; width: number; height: number; opacity: number } | null },
      data: { url: string; left: number; top: number; width: number; height: number } | null,
      opacity: number,
    ) => {
      if (!host || !bg) return;
      if (!data || opacity <= 0.0001) {
        if (geomRef.current && geomRef.current.opacity !== 0) {
          geomRef.current.opacity = 0;
          bg.style.opacity = "0";
        } else if (!geomRef.current) {
          bg.style.opacity = "0";
        }
        return;
      }

      if (urlRef.current !== data.url) {
        urlRef.current = data.url;
        // SQUARE_SM: this halo is under blur-2xl saturate-125 brightness-90 —
        // only ever shown blurred, so the square CDN crop is invisible and
        // `bg-cover` was already cropping it client-side anyway. Sharing the
        // thumbnail rung means the same photo's cart / saved / order thumbnail
        // warms it. One transform per url change (guarded above), never per
        // frame. @see lib/images/presets.ts SQUARE_SM ("the ambient halo").
        bg.style.backgroundImage = `url(${imagePresetUrl(data.url, SQUARE_SM)})`;
      }

      const last = geomRef.current;
      const nextLeft = data.left;
      const nextTop = data.top;
      const nextWidth = data.width;
      const nextHeight = data.height;
      const nextOpacity = opacity;

      if (!last || Math.abs(last.width - nextWidth) > 0.1) host.style.width = `${nextWidth}px`;
      if (!last || Math.abs(last.height - nextHeight) > 0.1) host.style.height = `${nextHeight}px`;
      if (!last || Math.abs(last.left - nextLeft) > 0.1 || Math.abs(last.top - nextTop) > 0.1) {
        host.style.transform = `translate3d(${nextLeft}px, ${nextTop}px, 0)`;
      }
      if (!last || Math.abs(last.opacity - nextOpacity) > 0.001) bg.style.opacity = String(nextOpacity);

      if (last) {
        last.left = nextLeft;
        last.top = nextTop;
        last.width = nextWidth;
        last.height = nextHeight;
        last.opacity = nextOpacity;
      } else {
        geomRef.current = { left: nextLeft, top: nextTop, width: nextWidth, height: nextHeight, opacity: nextOpacity };
      }
    };

    applyLayer(haloAHostRef.current, haloABgRef.current, haloAUrlRef, haloAGeomRef, primary, primaryOpacity);
    applyLayer(haloBHostRef.current, haloBBgRef.current, haloBUrlRef, haloBGeomRef, secondary, secondaryOpacity);
  }, [haloEnabled, listings]);

  useEffect(() => {
    if (isMobile) return;
    const idx = Math.min(Math.max(currentIndex, 0), Math.max(0, listings.length - 1));
    const urls = [listings[idx]?.image_urls?.[0] ?? null, listings[idx + 1]?.image_urls?.[0] ?? null, listings[idx - 1]?.image_urls?.[0] ?? null].filter(
      (u): u is string => Boolean(u),
    );
    let cancelled = false;
    for (const url of urls) {
      const img = document.createElement("img");
      // Resolve: a bare storage path would prefetch (and 404) against the site
      // domain, so the decode-ahead would warm nothing.
      img.src = resolveStorageUrl(url);
      const decode = (img as any).decode?.bind(img) as (() => Promise<void>) | undefined;
      if (decode) {
        decode().catch(() => {});
      }
      if (cancelled) break;
    }
    return () => {
      cancelled = true;
    };
  }, [currentIndex, isMobile, listings]);

  useLayoutEffect(() => {
    if (!haloEnabled) return;
    updateHaloFromScroll();
    const raf = requestAnimationFrame(updateHaloFromScroll);
    return () => cancelAnimationFrame(raf);
  }, [updateHaloFromScroll, currentIndex, haloEnabled]);

  useEffect(() => {
    if (!haloEnabled) return;
    const onResize = () => updateHaloFromScroll();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, [haloEnabled, updateHaloFromScroll]);

  const { saved, loading: saving, toggleSaved } = useSavedState({
    itemId: currentListing?.id ?? "",
    itemType: "listing",
    initialSaved: currentListing?.initialSaved ?? false,
    currentUserId,
  });

  const currentIndexRef = useRef(currentIndex);
  useEffect(() => {
    currentIndexRef.current = currentIndex;
    sessionStorage.setItem(snapIndexStorageKey, String(currentIndex));
  }, [currentIndex, snapIndexStorageKey]);

  // Latest-ref for the desktop `openPanel` call below — `getShowDetailsChange`
  // caches ONE closure per idx (see its own comment) that must read the
  // CURRENT listing id at call time, not whatever was current when that idx's
  // closure was first created.
  const currentListingIdRef = useRef<string | null>(currentListing?.id ?? null);
  useEffect(() => {
    currentListingIdRef.current = currentListing?.id ?? null;
  }, [currentListing?.id]);

  const listingsLengthRef = useRef(listings.length);
  useEffect(() => {
    listingsLengthRef.current = listings.length;
  }, [listings.length]);

  const effectiveLengthRef = useRef(effectiveLength);
  useEffect(() => {
    effectiveLengthRef.current = effectiveLength;
  }, [effectiveLength]);

  const openDetailsPanelDeferred = useCallback((listingId: string) => {
    startTransition(() => openPanel({ kind: "feed-details", listingId }));
  }, [openPanel]);

  const getShowDetailsChange = useCallback((idx: number) => {
    const existing = showDetailsCallbacksRef.current.get(idx);
    if (existing) return existing;
    const cb = (show: boolean) => {
      if (!show) return;
      if (idx !== currentIndexRef.current) return;
      const listingId = currentListingIdRef.current;
      if (!listingId) return;
      openDetailsPanelDeferred(listingId);
    };
    showDetailsCallbacksRef.current.set(idx, cb);
    return cb;
  }, [openDetailsPanelDeferred]);

  const scrollRafRef = useRef<number | null>(null);
  const hasRestoredRef = useRef(false);

  // Demand-funnel B5: the ACTIVE snap card is an impression, immediately
  // (O5 — no dwell requirement on the snap feed; the snap mechanism itself
  // guarantees a single dominant card). `currentIndex` is the active-card
  // state — updated by the scroll handler (Math.round(scrollTop/height)),
  // arrow-key/button navigation and session restore — and `currentListing`
  // derives from it, so keying the effect on the active listing id covers
  // every navigation path with one hook. position read from the ref so the
  // tail-placeholder clamp (index past listings.length while loading more)
  // doesn't re-run the effect for the same listing; scroll jitter across the
  // same card within 60s is absorbed by the buffer throttle anyway.
  //
  // Restore guard: on re-entry with a saved snap index > 0, the first render
  // briefly derives currentListing from index 0 before the restore effect
  // (below, hasRestoredRef) jumps to the saved card — recording that phantom
  // card 0 would credit an impression the buyer never saw. Skip until the
  // restore has run whenever a jump is pending. (Known micro-edge: saved
  // index > 0 that clamps back to 0 because the list shrank to one page —
  // card 0's impression is then skipped once; acceptable for a trend metric.)
  const currentListingId = currentListing?.id ? String(currentListing.id) : null;
  useEffect(() => {
    if (!currentListingId) return;
    if (!hasRestoredRef.current && savedIndexRef.current > 0) return;
    recordImpression({
      listingId: currentListingId,
      surface: "feed_snap",
      position: currentIndexRef.current,
    });
  }, [currentListingId]);

  const handleScroll = useCallback(() => {
    const el = containerRef.current;
    if (!el) return;
    if (scrollRafRef.current !== null) return;

    scrollRafRef.current = requestAnimationFrame(() => {
      scrollRafRef.current = null;
      const h = el.clientHeight;
      if (!h) return;
      const idx = Math.round(el.scrollTop / h);
      if (haloEnabled) updateHaloFromScroll();
      const prev = currentIndexRef.current;
      const max = effectiveLengthRef.current;
      // Skip scroll-driven index updates until restore is done
      if (!hasRestoredRef.current) return;
      if (idx !== prev && idx >= 0 && idx < max) {
        currentIndexRef.current = idx;
        setCurrentIndex(idx);
      }

      if (!hasNextPage) return;
      if (isLoadingMore) return;
      const realMax = listingsLengthRef.current;
      if (realMax <= 0) return;

      if (idx < Math.max(0, realMax - 3)) {
        tailLoadMoreArmedRef.current = true;
        return;
      }

      if (!tailLoadMoreArmedRef.current) return;
      if (idx >= Math.max(0, realMax - 2)) {
        tailLoadMoreArmedRef.current = false;
        onLoadMoreAction();
      }
    });
  }, [haloEnabled, hasNextPage, isLoadingMore, onLoadMoreAction, updateHaloFromScroll]);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    el.addEventListener("scroll", handleScroll, { passive: true });
    return () => el.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  

  useEffect(() => {
    return () => {
      if (scrollRafRef.current !== null) {
        cancelAnimationFrame(scrollRafRef.current);
        scrollRafRef.current = null;
      }
    };
  }, []);

  // Restore scroll position from sessionStorage on first load
  useEffect(() => {
    if (hasRestoredRef.current) return;
    if (listings.length === 0) return;
    const el = containerRef.current;
    if (!el) return;
    hasRestoredRef.current = true;
    const clamped = Math.min(savedIndexRef.current, listings.length - 1);
    if (clamped > 0) {
      const h = el.clientHeight;
      currentIndexRef.current = clamped;
      setCurrentIndex(clamped);
      el.scrollTo({ top: clamped * h, behavior: "instant" });
    }
  }, [listings.length]);

  useEffect(() => {
    if (!showDetailsPanel || !currentListing) {
      setDetailsLoading(false);
    }
  }, [showDetailsPanel, currentListing?.id]);

  // Follow scroll while open: pre-migration, `showDetailsPanel` was a plain
  // boolean unrelated to WHICH listing was showing — the panel stayed open
  // across a scroll and its content cross-faded to the new `currentListing`
  // (the effect below, `contentOpacity`). Reproduce that here by re-pointing
  // the FSM's stored `listingId` at the newly active listing whenever it
  // drifts from what's displayed, instead of closing and reopening — closing
  // would flicker the panel (via the strict-match card semantics elsewhere)
  // and would incorrectly read as "the user closed it" to other overlay
  // consumers for one render.
  //
  // MUST be `updatePanel`, not `openPanel`: this is content drift of an
  // ALREADY-open panel, not a new user-initiated panel context. `openPanel`'s
  // policy unconditionally clears the utility slot (§C.1 — "a new panel
  // context invalidates the sheet above it"), which is correct for a real
  // open but wrong here — it would force-close an open cart on every scroll
  // step while details are showing (`{ panel: feed-details, utility: cart }`
  // is legal, spec §E 1.5 item 7), running cart's close/cleanup and releasing
  // reservations as a side effect of the user merely scrolling. `updatePanel`
  // replaces only the panel payload, same `kind` guard, utility untouched.
  useEffect(() => {
    if (isMobile) return;
    if (!currentListing) return;
    if (overlay.panel.kind !== "feed-details") return;
    if (overlay.panel.listingId === currentListing.id) return;
    updatePanel({ kind: "feed-details", listingId: currentListing.id });
  }, [isMobile, currentListing, overlay.panel, updatePanel]);

  // Unmount cleanup: `showDetailsPanel` is derived, not owned — if this feed
  // instance unmounts (tab switch away from "Following") while its desktop
  // panel is open, nothing else resets `overlay.panel` back to `none`. Left
  // unhandled, that would leave the shell believing feed-details are still
  // open (mis-hiding the bottom nav on `/feed`, blocking other panels) after
  // the component that owned it is long gone.
  const showDetailsPanelRef = useRef(showDetailsPanel);
  useEffect(() => {
    showDetailsPanelRef.current = showDetailsPanel;
  }, [showDetailsPanel]);
  useEffect(() => {
    return () => {
      if (showDetailsPanelRef.current) closePanel();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollToIndex = useCallback((idx: number) => {
    const el = containerRef.current;
    if (!el) return;
    const next = Math.min(Math.max(idx, 0), Math.max(0, effectiveLength - 1));
    const h = el.clientHeight;
    el.scrollTo({ top: next * h, behavior: "smooth" });
  }, [effectiveLength]);

  useLayoutEffect(() => {
    if (!onRegisterScrollToTopAction) return;
    onRegisterScrollToTopAction(() => scrollToIndex(0));
    return () => onRegisterScrollToTopAction(null);
  }, [onRegisterScrollToTopAction, scrollToIndex]);

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (isInteracting) return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || (e.target as HTMLElement)?.isContentEditable) return;
      if (e.key === "ArrowDown" || e.key === "j") {
        e.preventDefault();
        scrollToIndex(currentIndex + 1);
      } else if (e.key === "ArrowUp" || e.key === "k") {
        e.preventDefault();
        scrollToIndex(currentIndex - 1);
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [currentIndex, isInteracting, scrollToIndex]);

  useEffect(() => {
    const node = sentinelRef.current;
    const root = containerRef.current;
    if (!node || !root) return;
    if (!hasNextPage) return;
    if (isLoadingMore) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          if (isLoadingMore) return;
          onLoadMoreAction();
        }
      },
      { root, rootMargin: "800px" },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isLoadingMore, onLoadMoreAction]);

  useEffect(() => {
    tailLoadMoreArmedRef.current = true;
  }, [listings.length]);

  useEffect(() => {
    const root = containerRef.current;
    if (!root) return;
    if (!hasNextPage) return;
    if (isLoadingMore) return;
    const lastIdx = listings.length - 1;
    if (lastIdx < 0) return;
    const node = cardNodeByIndexRef.current.get(lastIdx) ?? null;
    if (!node) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (!entry) return;
        if (!entry.isIntersecting) {
          tailLoadMoreArmedRef.current = true;
          return;
        }
        if (!tailLoadMoreArmedRef.current) return;
        tailLoadMoreArmedRef.current = false;
        if (isLoadingMore) return;
        onLoadMoreAction();
      },
      { root, rootMargin: isMobile ? "800px" : "200px" },
    );

    observer.observe(node);
    return () => observer.disconnect();
  }, [hasNextPage, isLoadingMore, isMobile, listings.length, onLoadMoreAction]);

  const handleSave = async () => {
    if (!currentListing) return;
    if (!currentUserId) {
      setShowBookmarkModal(true);
      return;
    }
    const next = !saved;
    setSavesCount((c) => Math.max(0, c + (next ? 1 : -1)));
    try {
      await toggleSaved();
      onToggleSavedAction();
    } catch (err) {
      console.error("Save error:", err);
      setSavesCount((c) => Math.max(0, c + (saved ? 1 : -1)));
    }
  };

  // Seed the saved count from the feed payload (server computes it in SQL and
  // ships it on the item — ListingForVerticalCard.saves_count) instead of each
  // active listing firing its own browser->Supabase count query. Re-seeds when
  // the active listing changes; the overlay-fade `displayedSavesCount`
  // machinery below reads this state unchanged. Optimistic +-1 lives in
  // handleSave. Absent field (RPC soft-fail) → 0.
  useEffect(() => {
    if (!currentListing) return;
    setSavesCount(currentListing.saves_count ?? 0);
  }, [currentListing?.id, currentListing?.saves_count]);

  useEffect(() => {
    if (!currentListing || !showDetailsPanel) {
      if (!showDetailsPanel) {
        setDisplayedListingId(null);
        setContentOpacity(1);
      }
      return;
    }
    
    if (displayedListingId === null) {
      setDisplayedListingId(currentListing.id);
      setContentOpacity(1);
      return;
    }
    
    if (displayedListingId !== currentListing.id) {
      setContentOpacity(0);
      let raf: number | null = null;
      const timer = setTimeout(() => {
        setDisplayedListingId(currentListing.id);
        raf = requestAnimationFrame(() => {
          setContentOpacity(1);
        });
      }, 150);
      return () => {
        clearTimeout(timer);
        if (raf !== null) cancelAnimationFrame(raf);
      };
    }
    setContentOpacity(1);
  }, [currentListing?.id, showDetailsPanel, displayedListingId]);

  useEffect(() => {
    if (!currentListing) {
      if (displayedActionsListingId !== null) {
        setActionsOpacity(0);
        const timer = setTimeout(() => {
          setDisplayedActionsListingId(null);
        }, 150);
        return () => clearTimeout(timer);
      }
      return;
    }

    if (displayedActionsListingId === null) {
      setDisplayedActionsListingId(currentListing.id);
      setDisplayedSaved(saved);
      setDisplayedSavesCount(savesCount);
      setDisplayedAvatarUrl(currentAvatarUrl);
      setDisplayedIsOwner(currentIsOwner);
      setDisplayedSellerUsername(currentSellerUsername);
      setDisplayedSellerId(currentListing.user_id);
      setDisplayedSellerIsFollowing(Boolean(currentListing.sellerIsFollowing));
      setActionsOpacity(1);
      return;
    }

    if (displayedActionsListingId !== currentListing.id) {
      setActionsOpacity(0);
      let raf: number | null = null;
      const timer = setTimeout(() => {
        setDisplayedActionsListingId(currentListing.id);
        setDisplayedSaved(saved);
        setDisplayedSavesCount(savesCount);
        setDisplayedAvatarUrl(currentAvatarUrl);
        setDisplayedIsOwner(currentIsOwner);
        setDisplayedSellerUsername(currentSellerUsername);
        setDisplayedSellerId(currentListing.user_id);
        setDisplayedSellerIsFollowing(Boolean(currentListing.sellerIsFollowing));
        raf = requestAnimationFrame(() => {
          setActionsOpacity(1);
        });
      }, 150);
      return () => {
        clearTimeout(timer);
        if (raf !== null) cancelAnimationFrame(raf);
      };
    } else {
      setDisplayedSaved(saved);
      setDisplayedSavesCount(savesCount);
      setDisplayedAvatarUrl(currentAvatarUrl);
      setDisplayedIsOwner(currentIsOwner);
      setDisplayedSellerUsername(currentSellerUsername);
      setDisplayedSellerId(currentListing.user_id);
      setDisplayedSellerIsFollowing(Boolean(currentListing.sellerIsFollowing));
      setActionsOpacity(1);
    }
  }, [
    currentListing?.id,
    saved,
    savesCount,
    currentAvatarUrl,
    currentIsOwner,
    currentSellerUsername,
    currentListing?.user_id,
    currentListing?.sellerIsFollowing,
    displayedActionsListingId,
  ]);

  const openChatForListing = useCallback((listing: ListingForVerticalCard) => {
    if (!currentUserId) {
      setShowAuthModal(true);
      return;
    }
    if (!listing.user_id || listing.user_id === currentUserId) {
      return;
    }
    const roomName = [currentUserId, listing.user_id].sort().join(":");
    setChatRoomName(roomName);
    setChatOtherProfile({ username: listing.sellerUsername ?? "seller", avatar_url: listing.sellerAvatarUrl ?? null });
    setChatInitialDraft({
      listing: {
        id: listing.id,
        user_id: listing.user_id,
        title: listing.title,
        description: listing.description,
        price: listing.price,
        currency: listing.currency ?? null,
        quantity: listing.quantity,
        image_urls: listing.image_urls,
        shipping_config: null,
        condition: listing.condition,
        grade: listing.grade,
        // ADR_LISTING_SHIP_FROM_ADDRESS Phase 2: drop free-text `location`.
        // The chat-draft listing carries only the fields the chat sheet
        // renders — ship-from country is sourced from the structured trio
        // (resolved server-side) on the chat-message payload.
        scheduled_publish_at: listing.scheduled_publish_at ?? null,
        seller_username: listing.sellerUsername ?? "seller",
        seller_avatar_url: listing.sellerAvatarUrl ?? null,
        // Phase 4 (2026-06-29) of shippo-listing-discriminator: visibility enum replaces is_chat_private
        visibility: "public",
        chat_private_cancelled_at: null,
        deleted_at: null,
      },
      sendImmediately: false,
    });
    if (isMobile) {
      setChatMode("direct");
      setChatOpen(true);
    } else {
      setChatOpen(true);
    }
  }, [currentUserId, isMobile]);

  const handleShareClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    (globalThis as any).__kraahlIgnoreNextCardClickAt = Date.now();
  };

  return (
    <>
      <AuthModal
        isOpen={showAuthModal}
        onCloseAction={() => setShowAuthModal(false)}
        username={currentSellerUsername}
        userImage={currentAvatarUrl || undefined}
        message="Connect to ask a question to the seller"
      />

      {bookmarkListing ? (
        <BookmarkLoginModal
          open={showBookmarkModal}
          onClose={() => setShowBookmarkModal(false)}
          listing={bookmarkListing}
        />
      ) : null}

      {bookmarkListing ? (
        <BookmarkLoginModal
          open={showBuyNowModal}
          onClose={() => setShowBuyNowModal(false)}
          listing={bookmarkListing}
          variant="buyNow"
        />
      ) : null}

      {haloEnabled ? (
        <>
          <div
            ref={haloAHostRef}
            className="hidden md:block fixed left-0 top-0 pointer-events-none z-0 overflow-visible"
            style={{ width: 0, height: 0, transform: "translate3d(0px, 0px, 0)", willChange: "transform, width, height" }}
          >
            <div
              ref={haloABgRef}
              className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl saturate-125 brightness-90 rounded-[28px]"
              style={{ opacity: 0, willChange: "opacity, transform, filter" }}
            />
          </div>
          <div
            ref={haloBHostRef}
            className="hidden md:block fixed left-0 top-0 pointer-events-none z-0 overflow-visible"
            style={{ width: 0, height: 0, transform: "translate3d(0px, 0px, 0)", willChange: "transform, width, height" }}
          >
            <div
              ref={haloBBgRef}
              className="absolute inset-0 bg-cover bg-center scale-110 blur-2xl saturate-125 brightness-90 rounded-[28px]"
              style={{ opacity: 0, willChange: "opacity, transform, filter" }}
            />
          </div>
        </>
      ) : null}

      {currentUserId && chatRoomName ? (
        isMobile ? (
          <ChatTabSlider
            open={chatOpen}
            onOpenChangeAction={setChatOpen}
            currentUserId={currentUserId}
            mode={chatMode === "direct" ? "chat" : "inbox"}
            roomName={chatMode === "direct" ? chatRoomName : undefined}
            initialOtherProfile={chatOtherProfile}
            initialDraft={chatInitialDraft}
          />
        ) : (
          <ChatDialog
            open={chatOpen}
            onOpenChangeAction={setChatOpen}
            roomName={chatRoomName}
            username={currentUserId}
            initialDraft={chatInitialDraft}
          />
        )
      ) : null}

      <div
        className="relative z-10 h-full w-full md:h-[calc(100dvh-var(--contest-banner-height,0px)-var(--custom-banner-height,0px))] md:flex md:items-center md:justify-center md:gap-4"
        style={{
          paddingRight: (showDetailsPanel && !isNarrowViewport) ? "min(calc(420px + 1rem), max(0px, calc(100vw - 640px)))" : undefined,
          transition: "padding-right 500ms ease-out",
        }}
      >
        <div className="relative h-full w-full md:w-[480px] md:shrink-0">
          <div
            ref={setContainerRef}
            data-snap-feed-container
            className="relative z-10 h-full w-full overflow-y-scroll snap-y snap-mandatory scrollbar-hide md:w-[520px] md:px-[80px] md:shrink-0 md:overflow-x-visible"
            style={{ scrollbarWidth: "none", msOverflowStyle: "none" }}
          >
            {listings.map((listing, idx) => {
              const inRenderWindow = Math.abs(idx - currentIndex) <= 1;
              return (
              <div
                key={listing.id}
                ref={(node) => {
                  cardNodeByIndexRef.current.set(idx, node);
                }}
                className="h-full w-full snap-start snap-always flex items-center justify-center shrink-0"
              >
                {inRenderWindow ? (
                  <>
                <div className="relative h-full w-full md:hidden">
                  <div className="relative h-full w-full overflow-hidden bg-black">
                    <MarketplaceVerticalListingCard
                      listing={listing}
                      currentUserId={currentUserId}
                      onFollowChangeAction={onFollowChangeAction}
                      onToggleSavedAction={onToggleSavedAction}
                      isActive={idx === currentIndex}
                      onInteractionStartAction={handleInteractionStart}
                      onInteractionEndAction={handleInteractionEnd}
                      onMessageAction={openChatForListing}
                      showDetails={showDetailsPanel && idx === currentIndex}
                      onShowDetailsChange={getShowDetailsChange(idx)}
                      onMobileSheetChange={idx === currentIndex ? onMobileSheetChangeAction : undefined}
                    />
                  </div>
                </div>

                <div className="hidden md:flex relative group items-center justify-center h-full w-full overflow-visible">
                  <div
                    className="relative w-[360px] h-[600px] transition-transform duration-300 ease-out"
                    style={showDetailsPanel && idx === currentIndex ? { transform: `translateX(${detailsShiftX}px)` } : undefined}
                  >
                    {listing.image_urls?.length ? (
                      <div
                        className="absolute inset-0 scale-105 blur-2xl opacity-20 rounded-3xl pointer-events-none"
                        style={{
                          // SQUARE_SM: only ever shown under `blur-2xl` at 20 %
                          // opacity, and `background-size: cover` already
                          // cropped it client-side, so the square CDN crop is
                          // free. @see lib/images/presets.ts SQUARE_SM.
                          backgroundImage: `url(${imagePresetUrl(listing.image_urls[0], SQUARE_SM)})`,
                          backgroundSize: "cover",
                          backgroundPosition: "center",
                        }}
                      />
                    ) : null}
                    <div className="relative z-10 h-full w-full rounded-[28px] overflow-hidden bg-black border border-border shadow-[0_8px_40px_rgba(0,0,0,0.35)] dark:border-white/10">
                      <MarketplaceVerticalListingCard
                        listing={listing}
                        currentUserId={currentUserId}
                        onFollowChangeAction={onFollowChangeAction}
                        onToggleSavedAction={onToggleSavedAction}
                        isActive={idx === currentIndex}
                        onInteractionStartAction={handleInteractionStart}
                        onInteractionEndAction={handleInteractionEnd}
                        onMessageAction={openChatForListing}
                        showDetails={showDetailsPanel && idx === currentIndex}
                        onShowDetailsChange={getShowDetailsChange(idx)}
                        onMobileSheetChange={idx === currentIndex ? onMobileSheetChangeAction : undefined}
                    showSellerHeader={false}
                      />
                    </div>
                  </div>
                </div>
                  </>
                ) : (
                  <div className="h-full w-full bg-black" />
                )}
              </div>
              );
            })}

            {tailPlaceholderCount ? (
              <div
                key="tail-placeholder"
                ref={(node) => {
                  cardNodeByIndexRef.current.set(listings.length, node);
                }}
                className="h-full w-full snap-start snap-always flex items-center justify-center shrink-0"
                aria-hidden
              >
                <div className="relative h-full w-full md:hidden">
                  <div className="relative h-full w-full bg-black">
                    <Skeleton aria-hidden className="absolute inset-0 rounded-none bg-muted dark:bg-white/5" />
                    <div className="absolute inset-0 bg-linear-to-b from-black/20 via-transparent to-black/55" />
                    <div className="absolute left-3 z-10 flex items-center gap-2 top-[var(--feed-top-bar-height,4rem)]">
                      <Skeleton className="h-9 w-9 shrink-0 rounded-full bg-muted dark:bg-white/10" />
                      <div className="flex flex-col items-start gap-1">
                        <Skeleton className="h-4 w-24 rounded-full bg-muted dark:bg-white/10" />
                        <Skeleton className="h-3 w-16 rounded-full bg-muted dark:bg-white/10" />
                      </div>
                    </div>
                    <div className={`absolute right-3 z-20 flex flex-col items-center gap-2 pt-[calc(var(--feed-top-bar-height,4rem)+4rem)] bottom-56`}>
                      <Skeleton className="h-9 w-9 rounded-full bg-muted dark:bg-white/10" />
                      <Skeleton className="h-9 w-9 rounded-full bg-muted dark:bg-white/10" />
                      <Skeleton className="h-9 w-9 rounded-full bg-muted dark:bg-white/10" />
                      <Skeleton className="h-9 w-9 rounded-full bg-muted dark:bg-white/10" />
                      <Skeleton className="h-9 w-9 rounded-full bg-muted dark:bg-white/10" />
                    </div>
                    <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3">
                      <div className="flex items-center gap-2 min-w-0">
                        <Skeleton className="h-5 flex-1 min-w-0 max-w-[60%] rounded-lg bg-muted dark:bg-white/10" />
                        <Skeleton className="h-4 w-4 shrink-0 rounded-full bg-muted dark:bg-white/10" />
                        <div className="flex items-center gap-2 shrink-0">
                          <Skeleton className="h-6 w-14 rounded-full bg-muted dark:bg-white/10" />
                          <Skeleton className="h-6 w-12 rounded-full bg-muted dark:bg-white/10" />
                        </div>
                      </div>
                      <div className="flex items-center gap-3">
                        <Skeleton className="h-8 w-24 rounded-lg bg-muted dark:bg-white/10" />
                        <Skeleton className="h-11 flex-1 rounded-full bg-muted dark:bg-white/10" />
                      </div>
                    </div>
                  </div>
                </div>

                <div className="hidden md:flex relative group items-center justify-center h-full w-full overflow-visible">
                  <div className="relative w-[360px] h-[600px] transition-transform duration-300 ease-out">
                    <Skeleton aria-hidden className="absolute inset-0 scale-105 blur-2xl opacity-20 rounded-3xl bg-muted dark:bg-white/10" />
                    <div className="relative z-10 h-full w-full rounded-[28px] overflow-hidden bg-black border border-border shadow-[0_8px_40px_rgba(0,0,0,0.35)] dark:border-white/10">
                      <Skeleton className="absolute inset-0 bg-muted dark:bg-white/5" />
                      <div className="absolute left-3 top-3 flex items-center gap-2 z-10">
                        <Skeleton className="h-9 w-9 rounded-full bg-white/10" />
                        <div className="flex flex-col gap-1">
                          <Skeleton className="h-4 w-24 rounded-full bg-white/10" />
                          <Skeleton className="h-3 w-16 rounded-full bg-white/10" />
                        </div>
                      </div>
                      <div className="absolute right-3 top-[calc(4rem+4rem)] flex flex-col items-center gap-2 z-10">
                        <Skeleton className="h-9 w-9 rounded-full bg-white/10" />
                        <Skeleton className="h-9 w-9 rounded-full bg-white/10" />
                        <Skeleton className="h-9 w-9 rounded-full bg-white/10" />
                        <Skeleton className="h-9 w-9 rounded-full bg-white/10" />
                      </div>
                      <div className="absolute bottom-0 left-0 right-0 p-4 space-y-3">
                        <div className="flex items-center gap-2">
                          <Skeleton className="h-5 flex-1 max-w-[55%] rounded-lg bg-white/10" />
                          <Skeleton className="h-4 w-4 rounded-full bg-white/10" />
                          <Skeleton className="h-6 w-14 rounded-full bg-white/10" />
                          <Skeleton className="h-6 w-12 rounded-full bg-white/10" />
                        </div>
                        <div className="flex items-center gap-3">
                          <Skeleton className="h-8 w-24 rounded-lg bg-white/10" />
                          <Skeleton className="h-11 flex-1 rounded-full bg-white/10" />
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ) : null}

            {loadMoreSentinelRef ? <div ref={loadMoreSentinelRef} aria-hidden className="h-px" /> : null}
            <div ref={sentinelRef} aria-hidden className="h-px" />
          </div>
        </div>

        {currentListing ? (
          <div className="hidden md:flex w-28 shrink-0 flex-col items-center justify-center gap-2 z-50 md:-ml-8">
            <div className="flex flex-col items-center gap-5 mb-6">
              <div
                className={`transition-all duration-150 overflow-hidden ${showDetailsPanel ? "max-h-0 opacity-0 pointer-events-none" : "max-h-40 opacity-100"}`}
                style={{ opacity: showDetailsPanel ? 0 : actionsOpacity }}
                aria-hidden={showDetailsPanel}
              >
                <div className="flex flex-col items-center gap-1">
                  <Link
                    href={`/${displayedSellerUsername}`}
                    prefetch={true}
                    onClick={(e) => e.stopPropagation()}
                    className="relative w-12 h-12 rounded-full border-2 border-border overflow-hidden bg-muted/70 backdrop-blur-md transition-all duration-300 dark:border-white/40 dark:bg-white/10"
                    aria-label={`Open @${displayedSellerUsername}`}
                  >
                    {displayedAvatarUrl ? (
                      <Image
                        key={displayedActionsListingId}
                        // 48 px box -> SQUARE_SM. Sized at the render site, not
                        // where `displayedAvatarUrl` is set: the same value is
                        // forwarded below as `profileAvatarUrl` into a
                        // notification payload, which must carry the plain
                        // resolved URL rather than a transform of it.
                        src={imagePresetUrl(displayedAvatarUrl, SQUARE_SM)}
                        alt={displayedSellerUsername}
                        fill
                        sizes="48px"
                        className="object-cover transition-opacity duration-300"
                      />
                    ) : (
                      <div className="h-full w-full flex items-center justify-center">
                        <User className="w-5 h-5 text-muted-foreground dark:text-white/70" />
                      </div>
                    )}
                  </Link>

                  <div className="flex flex-col items-center gap-1">
                    <Link
                      key={displayedActionsListingId ?? displayedSellerUsername}
                      href={`/${displayedSellerUsername}`}
                      prefetch={true}
                      onClick={(e) => e.stopPropagation()}
                      className="text-foreground text-xs font-semibold hover:underline max-w-30 truncate md:overflow-visible md:text-clip animate-in fade-in duration-200 dark:text-white/90"
                    >
                      <span className="no-underline">@</span>
                      <span>{displayedSellerUsername}</span>
                    </Link>
                    {!displayedIsOwner && displayedSellerId ? (
                      <div onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
                        <UnifiedFollowButton
                          key={displayedActionsListingId ? `${displayedActionsListingId}-follow` : undefined}
                          currentUserId={currentUserId ?? null}
                          profileId={displayedSellerId}
                          initialFollowing={displayedSellerIsFollowing}
                          variant="compact"
                          context="carousel"
                          profileUsername={displayedSellerUsername}
                          profileAvatarUrl={displayedAvatarUrl ?? null}
                          promptNotificationsOnFollow={true}
                          onFollowChange={() => onFollowChangeAction()}
                          className="h-7"
                        />
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>

              <div className="flex flex-col items-center gap-1 transition-opacity duration-150" style={{ opacity: actionsOpacity }}>
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={saving}
                  aria-label={displayedSaved ? "Remove from saved" : "Save item"}
                  className="flex items-center justify-center"
                >
                  <div
                    className={`p-3 rounded-full transition-all duration-300 ${
                      displayedSaved
                        ? "bg-white text-black"
                        : "bg-muted text-foreground hover:bg-muted/80 dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
                    }`}
                  >
                    <Bookmark className={`w-6 h-6 ${displayedSaved ? "fill-current" : ""}`} />
                  </div>
                </button>
                {displayedSavesCount > 0 && (
                  <span className="text-xs font-medium text-foreground bg-muted/80 rounded-full px-2 py-0.5 min-w-6 text-center select-none transition-opacity duration-300 dark:text-white/90 dark:bg-white/10">
                    {displayedSavesCount}
                  </span>
                )}
              </div>

              <button type="button" onClick={() => { if (currentListing) openChatForListing(currentListing) }} aria-label="Ask seller a question" className="flex flex-col items-center gap-1">
                <div className="relative p-3 rounded-full bg-muted text-foreground hover:bg-muted/80 transition-colors dark:bg-white/10 dark:text-white dark:hover:bg-white/20">
                  <MessageCircle className="w-6 h-6" />
                  <span className="absolute inset-0 flex items-center justify-center text-[10px] font-bold leading-none">?</span>
                </div>
              </button>

              <ShareSheet
                url={`/listings/${currentListing.id}`}
                title={currentListing.title}
                image={currentListing.image_urls[0]}
                price={Number(currentListing.price)}
                currency={currentListing.currency ?? undefined}
                storyData={{ seller: currentListing.sellerUsername }}
                listingMeta={buildListingMetaFromCard(currentListing)}
                variant="item"
              >
                <button
                  type="button"
                  onClick={(e) => {
                    handleShareClick(e)
                  }}
                  aria-label="Share item"
                  data-stop-prop
                  className="flex flex-col items-center gap-1"
                >
                  <div className="p-3 rounded-full bg-muted text-foreground hover:bg-muted/80 transition-colors dark:bg-white/10 dark:text-white dark:hover:bg-white/20">
                    <Upload className="w-6 h-6" />
                  </div>
                </button>
              </ShareSheet>

              <button
                onClick={() => setReportDialogOpen(true)}
                className="flex flex-col items-center gap-1"
                aria-label="Report item"
              >
                <div className="p-3 rounded-full bg-muted text-muted-foreground hover:text-foreground hover:bg-muted/80 transition-colors dark:bg-white/10 dark:text-white/70 dark:hover:text-white dark:hover:bg-white/20">
                  <Flag className="w-5 h-5" />
                </div>
              </button>
            </div>

            <div className="flex flex-col items-center gap-2">
              <button
                type="button"
                onClick={() => scrollToIndex(currentIndex - 1)}
                disabled={currentIndex === 0}
                aria-label="Previous item"
                className="p-3 rounded-full bg-muted text-foreground hover:bg-muted/80 transition-all disabled:opacity-30 disabled:cursor-not-allowed dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
              >
                <ChevronUp className="w-6 h-6" />
              </button>

              <button
                type="button"
                onClick={() => scrollToIndex(currentIndex + 1)}
                disabled={currentIndex >= effectiveLength - 1}
                aria-label="Next item"
                className="p-3 rounded-full bg-muted text-foreground hover:bg-muted/80 transition-all disabled:opacity-30 disabled:cursor-not-allowed dark:bg-white/10 dark:text-white dark:hover:bg-white/20"
              >
                <ChevronDown className="w-6 h-6" />
              </button>
            </div>
          </div>
        ) : null}

        {currentListing && (() => {
          const panelBody = (
            <div className={isNarrowViewport
              ? "flex flex-col overflow-hidden max-h-[85dvh] text-white"
              : "w-full h-full max-h-[calc(100dvh-var(--feed-top-bar-height,4rem)-0.75rem)] bg-card text-foreground rounded-3xl flex flex-col overflow-hidden border border-border dark:border-white/10"
            }>
              <div className="relative p-4 border-b border-border dark:border-white/10 flex items-center gap-2">
                <Link
                  href={`/listings/${currentListing.id}`}
                  className="flex-1 min-w-0 pr-12 transition-opacity duration-150"
                  style={{ opacity: contentOpacity }}
                >
                  <h3 className="text-foreground dark:text-white font-semibold text-base line-clamp-1">
                    {currentListing.title}
                  </h3>
                </Link>
                <button
                  type="button"
                  onClick={(event) => {
                    event.preventDefault();
                    event.stopPropagation();
                    closePanel();
                  }}
                  onPointerDown={(event) => {
                    event.stopPropagation();
                  }}
                  onPointerUp={(event) => {
                    event.stopPropagation();
                    closePanel();
                  }}
                  className="absolute right-3 top-3 h-10 w-10 shrink-0 inline-flex items-center justify-center rounded-full hover:bg-muted transition-colors"
                  aria-label="Close"
                  style={{ touchAction: "manipulation" }}
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              <div className="isolate flex-1 overflow-y-auto hide-scrollbar">
                <div
                  className="sticky top-0 z-40 border-b border-border bg-card dark:border-white/10"
                  style={{ opacity: contentOpacity }}
                >
                  <div className="p-4 space-y-4 transition-opacity duration-150">
                    <div className="flex items-center gap-2 flex-wrap">
                      {currentIsAuction && (
                        <>
                          <span className="px-3 py-1 rounded-full bg-muted text-foreground text-xs font-medium dark:bg-white/10 dark:text-white">
                            Auction
                          </span>
                          {currentEffectiveAuctionStatus === "live" ? (
                            <span className="flex items-center gap-1.5 px-3 py-1 bg-green-500/20 text-green-400 rounded-full text-xs font-medium border border-green-500/30">
                              <span className="w-1.5 h-1.5 bg-green-400 rounded-full animate-pulse" />
                              Live
                            </span>
                          ) : (currentEffectiveAuctionStatus === "ended" || currentEffectiveAuctionStatus === "settled") ? (
                            <span className="flex items-center gap-1 px-3 py-1 bg-red-500/20 text-red-400 rounded-full text-xs font-medium">
                              <span className="w-1.5 h-1.5 bg-red-400 rounded-full" />
                              Ended
                            </span>
                          ) : (
                            <span className="px-3 py-1 rounded-full bg-amber-500/20 text-amber-400 text-xs font-medium">
                              Soon
                            </span>
                          )}
                        </>
                      )}
                      {!currentListing.card_details && currentListing.condition ? (
                        <span className="px-3 py-1 rounded-full bg-muted text-foreground text-xs font-medium dark:bg-white/10 dark:text-white">
                          {currentListing.condition.charAt(0).toUpperCase() + currentListing.condition.slice(1)}
                        </span>
                      ) : null}
                      {!currentListing.card_details && currentListing.grade && currentListing.condition?.toLowerCase() !== "new" && (
                        <span
                          className={`px-2 py-1 rounded-full text-white text-xs font-medium ${
                            Number(currentListing.grade) >= 9
                              ? "bg-emerald-500"
                              : Number(currentListing.grade) >= 7
                                ? "bg-amber-500"
                                : "bg-orange-500"
                          }`}
                        >
                          {currentListing.grade}/10
                        </span>
                      )}
                    </div>

                    {currentListing.card_details ? (() => {
                      const cd = currentListing.card_details;
                      const tcg: TcgCardDetailsData = {
                        listing_id: currentListing.id,
                        card_name: currentListing.title,
                        tcg_name: cd.tcg_name ?? null,
                        set_name: cd.set_name ?? null,
                        rarity_name: cd.rarity_name ?? null,
                        condition_name: cd.condition_name ?? null,
                        grading_company_name: cd.grading_company_name ?? null,
                        grade_label: cd.grade_label ?? null,
                        grade_value: cd.grade_value ?? null,
                        is_first_edition: (cd.badges ?? []).includes("firstEdition"),
                        is_shadowless: (cd.badges ?? []).includes("shadowless"),
                        is_reverse_holo: (cd.badges ?? []).includes("reverseHolo"),
                        is_stamped_promo: (cd.badges ?? []).includes("stampedPromo"),
                        is_misprint: (cd.badges ?? []).includes("misprint"),
                        is_alt_art: (cd.badges ?? []).includes("altArt"),
                        is_foil_variant: (cd.badges ?? []).includes("foil"),
                        is_autograph: (cd.badges ?? []).includes("autograph"),
                        is_signed: (cd.badges ?? []).includes("signed"),
                        is_patch_relic: (cd.badges ?? []).includes("patchRelic"),
                        is_rookie: (cd.badges ?? []).includes("rookie"),
                        is_foreign_black_border: (cd.badges ?? []).includes("foreignBlackBorder"),
                        defects: cd.defects ?? [],
                      };
                      return <TcgCardDetails card={tcg} className="mt-1" />;
                    })() : null}

                    <p className="text-foreground/90 dark:text-white/80 text-sm font-normal leading-relaxed">
                      {currentListing.description}
                    </p>

                    <div className="flex flex-col gap-2 pt-2">
                      {currentListing.shipFromAddress?.country ? (
                        <div className="flex items-center gap-2 text-muted-foreground dark:text-white/60 text-xs font-normal">
                          <MapPin className="w-3.5 h-3.5" />
                          <span>{getCountryName(currentListing.shipFromAddress.country)}</span>
                        </div>
                      ) : null}
                      <div className="flex items-center justify-between gap-2 text-muted-foreground dark:text-white/60 text-xs font-normal">
                        <div className="flex items-center gap-2 min-w-0">
                          <Clock className="w-3.5 h-3.5" />
                          <span>
                            {tListing("listed", {
                              time: (() => {
                                const now = Date.now();
                                const then = new Date(currentListing.created_at).getTime();
                                if (isNaN(then)) return new Date().toLocaleDateString("en-US");
                                const diff = Math.floor((now - then) / 1000);
                                if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
                                if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
                                if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
                                return new Date(currentListing.created_at).toLocaleDateString("en-US", {
                                  day: "2-digit",
                                  month: "short",
                                  year: "numeric",
                                });
                              })(),
                            })}
                          </span>
                        </div>
                        <Link
                          href={`/listings/${currentListing.id}`}
                          className="shrink-0 rounded-full bg-muted px-3 py-1 text-[11px] font-medium text-foreground hover:bg-accent transition-colors dark:bg-white/10 dark:text-white/80 dark:hover:bg-white/20 dark:hover:text-white"
                        >
                          {tListing("viewListing")}
                        </Link>
                      </div>
                    </div>

                    {!detailsLoading ? (
                      <OrderPartyCard
                        role="seller"
                        name={currentSellerUsername}
                        username={currentSellerUsername}
                        avatarUrl={currentListing.sellerAvatarUrl ?? null}
                        userId={currentListing.user_id}
                        currentUserId={currentUserId}
                        initialFollowing={Boolean(currentListing.sellerIsFollowing)}
                        onFollowChange={() => onFollowChangeAction()}
                        salesCount={currentListing.sellerSalesCount}
                        surface="plain"
                        messageVariant="secondary"
                        chatLayout="dialog"
                      />
                    ) : (
                      <OrderPartyCardSkeleton surface="plain" />
                    )}

                    <div className="pt-1">
                      {currentIsAuction && currentAuctionDisplayPrice != null ? (
                        <div className="space-y-3">
                          <div className="flex items-center justify-between gap-4">
                            <div className="flex flex-col">
                              <span className="text-xs font-medium text-muted-foreground dark:text-white/70">
                                {currentEffectiveHighestBid != null ? "Current bid" : "Starting price"}
                              </span>
                              <span className="text-foreground dark:text-white text-2xl font-bold shrink-0 translate-y-[-4px]">
                                {formatPriceLabel(String(currentAuctionDisplayPrice), currentListing.currency)}
                              </span>
                            </div>
                          </div>
                          <Button
                            variant="secondary"
                            size="default"
                            className="w-full"
                            onClick={(e) => {
                              e.stopPropagation();
                              openUtility({
                                kind: "auction-bid",
                                listingId: currentListing.id,
                                description: currentListing.description,
                              });
                            }}
                          >
                            {tAuction("quickBidTitle")}
                          </Button>
                        </div>
                      ) : (
                        <div className="flex min-w-0 items-center gap-2 sm:gap-4">
                          <div
                            className={cn(
                              "shrink-0",
                              canFeedSplit && "flex h-11 min-h-11 max-h-11 items-center",
                            )}
                          >
                            <span className="text-foreground dark:text-white text-2xl font-bold leading-none tabular-nums shrink-0 translate-y-[-4px]">
                              {formatPriceLabel(currentListing.price, currentListing.currency)}
                            </span>
                          </div>
                          <div
                            className="min-w-0 flex-1"
                            onClick={(e) => e.stopPropagation()}
                            onPointerDown={(e) => e.stopPropagation()}
                            onMouseDown={(e) => e.stopPropagation()}
                          >
                            {canFeedSplit ? (
                              <ListingSplitCommerceCta
                                unified={{
                                  listingId: currentListing.id,
                                  price: currentListing.price,
                                  currency: currentListing.currency ?? null,
                                  stock: currentListing.quantity,
                                  currentUserId,
                                  name: currentListing.title,
                                  images: currentListing.image_urls,
                                  sellerId: currentListing.user_id,
                                  sellerName: currentSellerUsername,
                                  sellerAvatarUrl: currentListing.sellerAvatarUrl ?? undefined,
                                  condition: currentListing.condition,
                                  description: currentListing.description,
                                  shippingMethods: [],
                                  variant: "default",
                                  context: "feed",
                                  uiVariant: "feed-light",
                                  uiLayout: "inline",
                                  lazy: false,
                                  scheduledPublishAt: currentListing.scheduled_publish_at ?? null,
                                  listingType: (currentListing as any).type ?? null,
                                  buyNow: true,
                                  onLoginRequiredForBuyNow: () => setShowBuyNowModal(true),
                                  splitBarCompact: true,
                                }}
                                offer={{
                                  listingId: currentListing.id,
                                  currentUserId: currentUserId ?? null,
                                  listingPrice: Number(currentListing.price),
                                  currency: currentListing.currency ?? null,
                                  listingTitle: currentListing.title,
                                  listingImageUrl: currentListing.image_urls[0] ?? null,
                                }}
                              />
                            ) : showOffersOnlyFeedCta ? (
                              <MakeOfferButton
                                listingId={currentListing.id}
                                currentUserId={currentUserId ?? null}
                                sellerId={currentListing.user_id}
                                listingFormat="offers_only"
                                listingPrice={Number(currentListing.price)}
                                currency={currentListing.currency ?? null}
                                listingTitle={currentListing.title}
                                listingImageUrl={currentListing.image_urls[0] ?? null}
                                listingCondition={currentListing.condition}
                                listingGrade={currentListing.grade}
                                sellerUsername={currentSellerUsername}
                                variant="feed"
                                fullWidth
                              />
                            ) : (
                              <UnifiedAddToCartButton
                                listingId={currentListing.id}
                                price={currentListing.price}
                                currency={currentListing.currency ?? null}
                                stock={currentListing.quantity}
                                currentUserId={currentUserId}
                                name={currentListing.title}
                                images={currentListing.image_urls}
                                sellerId={currentListing.user_id}
                                sellerName={currentSellerUsername}
                                sellerAvatarUrl={currentListing.sellerAvatarUrl ?? undefined}
                                condition={currentListing.condition}
                                description={currentListing.description}
                                shippingMethods={[]}
                                context="feed"
                                uiVariant="feed-light"
                                uiLayout="inline"
                                scheduledPublishAt={currentListing.scheduled_publish_at ?? null}
                                listingType={(currentListing as any).type ?? null}
                                listingFormat={currentListing.listing_format ?? null}
                                buyNow
                                onLoginRequiredForBuyNow={() => setShowBuyNowModal(true)}
                              />
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                <div className="p-4 space-y-4 transition-opacity duration-150" style={{ opacity: contentOpacity }}>
                  {detailsLoading && (
                    <div className="space-y-4">
                      <div className="mt-4">
                        <div className="-mx-4 px-4 mb-4">
                          <Skeleton className="h-7 w-56 rounded-full bg-muted dark:bg-white/10 animate-none" />
                        </div>
                        <ItemCardSkeletonRow
                          count={4}
                          keyPrefix="detail-panel-sk-a"
                          containerClassName="-mx-4 px-4 md:mx-0 md:px-0"
                          rowClassName="hide-scrollbar pb-2"
                          itemClassName="w-40 sm:w-40 md:w-40 lg:w-40"
                        />
                      </div>

                      <div className="mt-4">
                        <div className="-mx-4 px-4 mb-4">
                          <Skeleton className="h-7 w-48 rounded-full bg-muted dark:bg-white/10 animate-none" />
                        </div>
                        <ItemCardSkeletonRow
                          count={4}
                          keyPrefix="detail-panel-sk-b"
                          containerClassName="-mx-4 px-4 md:mx-0 md:px-0"
                          rowClassName="hide-scrollbar pb-2"
                          itemClassName="w-40 sm:w-40 md:w-40 lg:w-40"
                        />
                      </div>
                    </div>
                  )}
                  <VerticalListingSuggestions
                    currentListingId={currentListing.id}
                    sellerId={currentListing.user_id}
                    sellerUsername={currentSellerUsername}
                    currentUserId={currentUserId}
                    currentListingTitle={currentListing.title}
                    currentListingDescription={currentListing.description}
                    onLoadingChange={(loading) => {
                      if (!showDetailsPanel) return;
                      setDetailsLoading(loading);
                    }}
                    enabled={showDetailsPanel}
                  />
                </div>
              </div>
            </div>
          );
          if (isNarrowViewport) {
            return (
              <Dialog
                open={showDetailsPanel}
                onOpenChange={(open) => { if (!open) closePanel(); }}
              >
                <DialogContent
                  noTransform
                  overlayClassName={DIALOG_CARD_OVERLAY_CLASS}
                  className={cn(DIALOG_CARD_CONTENT_BASE, "p-0 max-w-[520px] w-[calc(100vw-2rem)] overflow-hidden")}
                >
                  <DialogTitle className="sr-only">{currentListing.title}</DialogTitle>
                  {panelBody}
                </DialogContent>
              </Dialog>
            );
          }
          return (
            <div
              className={`hidden md:block fixed transition-all duration-500 ease-out ${
                showDetailsPanel
                  ? "opacity-100 translate-x-0 pointer-events-auto"
                  : "opacity-0 translate-x-full pointer-events-none"
              }`}
              style={{
                top: "var(--feed-top-bar-height, 4rem)",
                right: "max(0px, calc(50vw - 522px))",
                height: "calc(100dvh - var(--feed-top-bar-height, 4rem))",
                width: "min(420px, max(280px, calc(100vw - 576px)))",
                padding: "0 0 0.75rem 1rem",
                zIndex: Z.overlayPanel,
              }}
            >
              {panelBody}
            </div>
          );
        })()}
      </div>

      {currentListing && (
        <ReportDialog
          open={reportDialogOpen}
          onOpenChangeAction={setReportDialogOpen}
          itemType="listing"
          itemId={currentListing.id}
          itemTitle={currentListing.title}
          currentUserId={currentUserId}
          reportedUserId={currentListing.user_id}
          reportedUsername={currentSellerUsername}
          reportedAvatarUrl={currentListing.sellerAvatarUrl ?? null}
        />
      )}
    </>
  );
}

function buildListingMetaFromCard(listing: ListingForVerticalCard): ListingShareMeta {
  return {
    listingFormat: listing.listing_format ?? null,
    scheduledPublishAt: listing.scheduled_publish_at ?? null,
    auction: listing.listing_format === "auction"
      ? {
          startPrice: listing.auction_start_price ?? null,
          highestBid: listing.auction_highest_bid ?? null,
          bidCount: listing.auction_bid_count ?? 0,
          endAt: listing.auction_end_at ?? null,
          status: listing.auction_status ?? null,
        }
      : null,
  };
}

function formatPriceLabel(price: string, currency?: string | null): string {
  const n = Number(price);
  if (!Number.isFinite(n)) return price;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currency || "USD",
      maximumFractionDigits: 0,
    }).format(n);
  } catch {
    return `${n.toLocaleString()}`;
  }
}

